import type { LoginOptions, LogoutOptions } from "@jetblue/kit-auth";
import { useAuth, useValidAccessToken } from "@jetblue/kit-auth-react";
import { useQuery } from "@tanstack/react-query";

const toError = (message: string, cause: unknown): Error =>
  Object.assign(new Error(message), { cause });

type UseKitAuth = {
  isAuthenticated: boolean;
  accessToken: string | undefined;
  error: Error | null;
  isLoading: boolean;
  handleLogin: (options?: LoginOptions) => Promise<void>;
  handleLogout: (options?: LogoutOptions) => Promise<void>;
};

export const useKitAuth = (): UseKitAuth => {
  const { isAuthenticated, isLoading, login, logout } = useAuth();

  const getValidAccessToken = useValidAccessToken();

  const tokenQuery = useQuery({
    gcTime: 0,
    queryFn: async () => {
      try {
        const token = await getValidAccessToken();
        return { failure: null, token: token || null };
      } catch (error) {
        return {
          failure: toError("kit-auth could not produce an access token", error),
          token: null,
        };
      }
    },
    queryKey: ["kit-auth-token", isAuthenticated],
    retry: false,
    staleTime: 0,
  });

  return {
    accessToken: tokenQuery.data?.token ?? undefined,
    error: tokenQuery.data?.failure ?? null,
    handleLogin: login,
    handleLogout: logout,
    isAuthenticated: isAuthenticated && Boolean(tokenQuery.data),
    isLoading: isLoading || tokenQuery.isLoading,
  };
};
